import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { RedirectWithToast } from '@/shared/ui/RedirectWithToast';
import { NAV_ITEMS } from './venueNav';

type VenueContextValue = {
  venueId: string;
};

const VenueContext = createContext<VenueContextValue | null>(null);

export function VenueProvider({ children }: { children?: ReactNode }) {
  const { venueId } = useParams<{ venueId: string }>();
  const value = useMemo<VenueContextValue | null>(() => (venueId ? { venueId } : null), [venueId]);
  return <VenueContext.Provider value={value}>{children}</VenueContext.Provider>;
}

/**
 * Wraps the `/loc/:venueId` workspace with UX-only guards (ADR 0015): a `:venueId` not
 * in `me.venues` redirects to the `/` resolver with a toast; a deep link to a feature the
 * user is not permitted to use at this otherwise-accessible Venue redirects to the Venue
 * home with a toast. These only keep a bad URL off a dead screen — the backend 403 remains the
 * authoritative boundary (ADR 0007), so the guards never gate beyond redirecting.
 */
export function VenueRoute() {
  const { t } = useTranslation('common');
  const { venueId } = useParams<{ venueId: string }>();
  const { venues, isLoading } = useAuth();
  const auth = useAuthorization();
  const { pathname } = useLocation();

  // Don't guard against a not-yet-loaded session: RequireAuth already holds the route until
  // /api/v1/auth/me resolves, but staying inert while loading keeps the guard safe wherever mounted.
  if (isLoading) return null;

  if (venueId && !venues.some((venue) => venue.id === venueId)) {
    return <RedirectWithToast to="/" message={t('workspace.guard.unknownVenue')} />;
  }

  if (venueId) {
    const feature = currentFeaturePath(pathname, venueId);
    const item = NAV_ITEMS.find((navItem) => navItem.path === feature);
    if (item?.permissions && !auth.canAny(item.permissions, { kind: 'venue', venueId })) {
      return (
        <RedirectWithToast to={`/loc/${venueId}`} message={t('workspace.guard.featureForbidden')} />
      );
    }
  }

  return (
    <VenueProvider>
      <Outlet />
    </VenueProvider>
  );
}

/** The feature segment of a workspace path: `/loc/<id>/shifts` → `shifts`, `/loc/<id>` → ''. */
function currentFeaturePath(pathname: string, venueId: string): string {
  const prefix = `/loc/${venueId}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';
  return rest.replace(/^\/+/, '').split('/')[0] ?? '';
}

export function useVenueContext(): VenueContextValue {
  const ctx = useContext(VenueContext);
  if (!ctx) {
    throw new Error('useVenueContext must be used inside a VenueProvider with a :venueId param');
  }
  return ctx;
}
